import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';

import { Close } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Actions';
import { Expand } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Controls';

import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import useOnClickOutside from 'hooks/use-on-click-outside';

import MenuExperienceFragmentClickHandler from 'cms-components/MenuExperienceFragmentClickHandler/MenuExperienceFragmentClickHandler';
import {
  ExperienceFragmentLocationData,
  SeasonalMenuExperienceFragment,
} from 'cms-components/ExperienceFragments/SeasonalMenuExperienceFragment';
import { setBodyNoScroll } from 'utils/scroll';
import { formatMenuClickEvent } from 'utils/tracking';
import { keyValues } from 'constants/keys';

import styles from './SeasonalMenuPanels.module.scss';

export type SeasonalMenuPanelsProps = {
  isOpen: boolean;
  onToggle: () => void;
  title: string;
  content: ExperienceFragmentLocationData[];
};

export const SeasonalMenuPanels = ({
  isOpen = false,
  onToggle,
  title,
  content,
}: SeasonalMenuPanelsProps) => {
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    setBodyNoScroll(isOpen);
  }, [isOpen]);

  const handleClose = () => {
    onToggle();

    menuButtonRef?.current?.focus?.();
  };

  const handleEscapeToClose = (event: React.KeyboardEvent) => {
    if (isOpen && event.key === keyValues.KEY_ESCAPE) {
      trackEvent(formatMenuClickEvent(`Seasonal | closed menu using escape`));
      handleClose();
    }
  };

  const handleButtonClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (isOpen) {
      handleClose();
      trackEvent(formatMenuClickEvent(`Seasonal | closed menu using ${event.currentTarget.id}`));
    } else {
      onToggle();
      trackEvent(formatMenuClickEvent('Seasonal'));
    }
  };

  useOnClickOutside(menuRef, () => {
    if (isOpen) onToggle();
  });

  return (
    <div ref={menuRef} role="none" data-testid="SeasonalMenu" onKeyDown={handleEscapeToClose}>
      <button
        aria-controls="SeasonalMenu"
        aria-expanded={isOpen}
        ref={menuButtonRef}
        aria-label={`Toggle ${title} menu`}
        className={classNames(styles.btnMenu, {
          [styles.menuActive]: isOpen,
        })}
        data-testid="seasonal-menu-desktop-button"
        id="seasonal-menu-button"
        onClick={handleButtonClick}
        type="button"
      >
        {title}
        <Expand className="icon" size="xsmall" />
      </button>
      <div>
        <div
          aria-label={`${title} with Waitrose`}
          className={classNames(styles.nav, {
            [styles.open]: isOpen,
          })}
          data-testid="seasonal-open"
          id="SeasonalMenu"
          role="region"
        >
          <div className={styles.panelWrapper}>
            <MenuExperienceFragmentClickHandler
              trackingPrefix="Seasonal"
              type="desktop"
              onClose={handleClose}
            >
              <SeasonalMenuExperienceFragment content={content} />
            </MenuExperienceFragmentClickHandler>
          </div>
          <button
            className={styles.close}
            data-testid="seasonal-menu-close"
            id="menu-close"
            onClick={handleButtonClick}
            type="button"
          >
            <span className={styles.srOnly}>Close</span>
            <Close className={styles.icon} size="small" />
          </button>
        </div>
      </div>
    </div>
  );
};
